import type { Preference } from '~/models';

import { useSchedule } from '~/composables/useSchedule';

const usePreferenceStore = defineStore('customer-preference', () => {
  const storedPreference = ref<Preference | null>(null);

  const preference = computed<Preference>(() => {
    if (!storedPreference.value) {
      throw new Error('Preference not found');
    }

    return storedPreference.value;
  });

  const set = (payload: Preference): void => {
    storedPreference.value = payload;
  };

  const moneySymbol = computed<string>(() => {
    return preference.value.general.country.currency.symbol;
  });

  const isRestaurantOpened = computed<boolean>(() => {
    if (!preference.value.website.isOnline) {
      return false;
    }

    return useSchedule(
      preference.value.website.takeaway.openingTimes.collection
    ).inService();
  });

  const inDeliveryTime = computed<boolean>(() => {
    if (!preference.value.general.takeaway.enableDelivery) {
      return false;
    }

    return useSchedule(
      preference.value.website.takeaway.openingTimes.delivery
    ).inService();
  });

  const openAt = computed<string>(() => {
    return useSchedule(
      preference.value.website.takeaway.openingTimes.collection
    ).todayTime();
  });

  const deliveryStartsAt = computed<string>(() => {
    return useSchedule(
      preference.value.website.takeaway.openingTimes.delivery
    ).todayTime();
  });

  const canonicalURL = computed<string>(() => {
    let link = preference.value.general.company.websiteUrl;

    if (!link) {
      return '';
    }

    if (!/^https?:\/\//i.test(link)) {
      link = 'https://' + link;
    }

    if (link.endsWith('/welcome')) {
      link = link.replace('/welcome', '');
    }

    return link;
  });

  const inMaintenance = computed<boolean>(() => {
    return storedPreference.value?.website.inMaintenance || false;
  });

  return {
    preference,
    set,
    canonicalURL,
    moneySymbol,
    isRestaurantOpened,
    inDeliveryTime,
    inMaintenance,
    openAt,
    deliveryStartsAt
  };
});

export { usePreferenceStore };
